import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Upload, Download, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const ImageCompressor = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [quality, setQuality] = useState([85]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [compressionStats, setCompressionStats] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.split('.').pop().toLowerCase();
      if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
        setError('Only JPG, PNG, and WebP images supported');
        return;
      }
      setFile(selectedFile);
      setError('');
      setCompressionStats(null);
      setDownloadUrl(null);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    }
  };

  const compressImage = async () => {
    if (!file) {
      setError('Please select an image');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/image/compress?quality=${quality[0]}`,
        formData,
        {
          responseType: 'blob'
          // Don't set Content-Type - let axios set it automatically with boundary
        }
      );

      console.log('Response headers:', response.headers);
      console.log('All headers:', Object.keys(response.headers));

      // Get compression stats from headers (axios lowercases header names)
      const originalSize = parseInt(response.headers['x-original-size'] || response.headers['X-Original-Size'] || '0');
      const compressedSize = parseInt(response.headers['x-compressed-size'] || response.headers['X-Compressed-Size'] || '0');
      const reduction = parseFloat(response.headers['x-reduction-percent'] || response.headers['X-Reduction-Percent'] || '0');

      console.log('Parsed stats:', { originalSize, compressedSize, reduction });

      setCompressionStats({
        originalSize: (originalSize / 1024).toFixed(2),
        compressedSize: (compressedSize / 1024).toFixed(2),
        reduction: reduction.toFixed(1)
      });

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      setDownloadUrl(url);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `compressed_${file.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to compress image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Image Compressor</h1>
        <p className="text-muted-foreground">
          Reduce image file size while maintaining quality
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Upload Image</CardTitle>
            <CardDescription>JPG, PNG, or WebP format</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleFileChange}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-12 w-12 text-muted-foreground" />
                  <p className="text-sm font-medium">
                    {file ? file.name : 'Click to upload image'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG, WebP
                  </p>
                </div>
              </label>
            </div>

            {file && (
              <div className="bg-muted p-3 rounded-lg text-sm">
                <p><span className="font-semibold">Original size:</span> {(file.size / 1024).toFixed(2)} KB</p>
              </div>
            )}

            <div>
              <Label>Quality: {quality[0]}%</Label>
              <Slider
                value={quality}
                onValueChange={setQuality}
                min={10}
                max={100}
                step={5}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lower quality = smaller file size
              </p>
            </div>

            <Button
              onClick={compressImage}
              disabled={!file || loading}
              className="w-full"
              size="lg"
            >
              <Download className="mr-2 h-5 w-5" />
              {loading ? 'Compressing...' : 'Compress & Download'}
            </Button>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {compressionStats && (
              <Alert>
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold">Compression successful!</p>
                    <p className="text-sm">Original: {compressionStats.originalSize} KB</p>
                    <p className="text-sm">Compressed: {compressionStats.compressedSize} KB</p>
                    <p className="text-sm text-green-600">Reduced by {compressionStats.reduction}%</p>
                    {downloadUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = downloadUrl;
                          link.download = `compressed_${file.name}`;
                          link.click();
                        }}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download Again
                      </Button>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
          </CardHeader>
          <CardContent>
            {preview ? (
              <div className="flex justify-center">
                <img
                  src={preview}
                  alt="Preview"
                  className="max-w-full max-h-96 rounded-lg"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
                <div className="text-center">
                  <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">Image preview will appear here</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Tips for Best Results</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p>• Quality 85% is optimal for most images (good balance)</p>
          <p>• Use 60-70% for web images where file size matters most</p>
          <p>• PNG files are optimized automatically</p>
          <p>• JPEG quality below 50% may show visible artifacts</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImageCompressor;
